import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { getAllRouteParams } from '../../lib/get-all-route-params';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { GalleryService } from '../../services/gallery.service';
import { RedirectService } from '../../services/redirect.service';
import { UserService } from '../../services/user.service';
import { getBaseWithoutKey } from '../libs/base-without-key';

@Injectable({
  providedIn: 'root',
})
export class GalleryMaintenanceGuard {
  private galleryService = inject(GalleryService);
  private redirectService = inject(RedirectService);
  private userService = inject(UserService);
  private platformId = inject<string>(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    if (isPlatformServer(this.platformId)) return true;
    await firstValueFrom(this.userService.getUser());

    const fullPath = getFullPath(next);

    if (this.config.env === 'local') return true;

    try {
      await this.galleryService.getPublicGalleryFromSnapshot(next, {
        ignore503Errors: true,
      });
      return this.redirectService.redirectTo(
        fullPath.filter(u => !['access', 'error', 'not-found', 'maintenance'].includes(u)),
        { queryParams: next.queryParams }
      );
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error;
      const params = getAllRouteParams(next);
      const idOrPath = params.galleryPath || params.galleryId;
      const { baseWithoutKey, rest } = getBaseWithoutKey(fullPath, idOrPath);
      if (error.status !== 503) {
        return this.redirectService.redirectTo([...baseWithoutKey, ...rest], { queryParams: next.queryParams });
      }
      return true;
    }
  }
}
